import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Blurb, SkeletonBlurb } from 'components/Containers';
import ReferralModal from 'components/modals/referral/SendReferralModal';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import { dateWithoutTimezone } from 'lib/date';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { PERMISSIONS } from 'models/Profile';
import ReferralClass from 'models/reviews/Referral';
import colors from 'styles/theme/colors';
import { Label, LabelSmallBold } from 'styles/typography';

const getReferralValues = (review?: ReferralClass) => {
  const { createdAt, updatedAt, group, reviewedBy } = review || {};

  if (review?.pending) {
    return {
      statusColors: { backgroundColor: colors.black10, color: colors.black75 },
      bodyText: `Referral sent to ${group?.name} on ${dateWithoutTimezone(createdAt)}`,
      buttonText: 'View Pending Referral',
    };
  }
  if (review?.accepted) {
    return {
      statusColors: { backgroundColor: colors.actionGreen, color: colors.white },
      bodyText: `Referral accepted by ${reviewedBy?.fullName} at ${group?.name} on ${dateWithoutTimezone(updatedAt)}`,
      buttonText: 'View Accepted Referral',
    };
  }
  if (review?.rejected) {
    return {
      statusColors: { backgroundColor: colors.accentRed, color: colors.white },
      bodyText: `Referral denied by ${reviewedBy?.fullName} at ${group?.name} on ${dateWithoutTimezone(updatedAt)}`,
      buttonText: 'Send New Referral',
    };
  }
  if (review?.canceled) {
    return {
      statusColors: { backgroundColor: colors.black75, color: colors.white },
      bodyText: `Referral canceled by ${reviewedBy?.fullName} at ${group?.name} on ${dateWithoutTimezone(updatedAt)}`,
      buttonText: 'Send New Referral',
    };
  }

  return {
    statusColors: { backgroundColor: colors.white, color: colors.black10 },
    bodyText: 'Send a referral to a provider in Olio',
    buttonText: 'Refer Patient',
  };
};

export interface ReferralType {
  episode: Episode;
  locationEpisode: LocationEpisode;
  review?: ReferralClass;
  loading: boolean;
}

export default function Referral({ episode, locationEpisode, review, loading }: ReferralType) {
  const { statusColors, bodyText, buttonText } = getReferralValues(review);

  const isStayArchived = useMemo(() => {
    return locationEpisode.archived || episode.archived;
  }, [locationEpisode.archived, episode.archived]);

  const [showReferralModal, setShowReferralModal] = useState(false);

  const navigate = useNavigate();

  const handleClick = () => {
    if (review?.committed) {
      navigate(`/patients/${episode.id}/referrals`);
    } else {
      setShowReferralModal(true);
    }
  };

  if (episode?.archived) return null;

  return (
    <>
      <AccessGuard
        accessor={PERMISSIONS.reviewReferralCreate}
        skipPermissionCheck={!!review}
        locationEpisode={locationEpisode}
        episode={episode}>
        {loading ? (
          <SkeletonReferral />
        ) : (
          <PaddedBlurb data-cy='referral'>
            <ReferralHeader>
              <Label fontSize='14px'>Referral</Label>
              {review && <Status {...statusColors}>{review.statusLabel}</Status>}
            </ReferralHeader>
            <ReferralBodyContainer>
              <ReferralBody>{bodyText}</ReferralBody>
            </ReferralBodyContainer>

            <AccessGuard
              accessor={PERMISSIONS.reviewReferralCreate}
              skipPermissionCheck={!!review?.committed}
              locationEpisode={locationEpisode}
              episode={episode}>
              <ReferralButton variant='outline-primary' fullWidth onClick={handleClick} disabled={isStayArchived}>
                {buttonText}
              </ReferralButton>
            </AccessGuard>
          </PaddedBlurb>
        )}
      </AccessGuard>

      {showReferralModal && (
        <ReferralModal
          onCancel={() => setShowReferralModal(false)}
          onSuccess={() => setShowReferralModal(false)}
          locationEpisode={locationEpisode}
          episode={episode}
          patientName={`${episode?.patient.name}`}
        />
      )}
    </>
  );
}

const PaddedBlurb = styled(Blurb)`
  padding: 24px;
`;

const ReferralHeader = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  line-height: 14px;
`;

const ReferralBodyContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }
`;

const ReferralBody = styled.div`
  font-size: 12px;
  line-height: 20px;
`;

const ReferralButton = styled(Button)`
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0.19px;
  margin-top: 16px;
  height: 32px;
  border-radius: 3px;
`;

const Status = styled(LabelSmallBold)<{ backgroundColor: string; color: string }>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  color: ${({ color }) => color};
  border-radius: 16px;
  margin-left: 8px;
  padding: 4px 8px;
`;

const SkeletonBlank = styled(SkeletonBlurb)<{ $width: number; $height: number; $rounding?: number; $margin?: number }>`
  border-radius: ${({ $rounding, $height }) => ($rounding ?? $height) + 'px'};
  margin-bottom: ${({ $margin }) => ($margin ?? 0) + 'px'};
  height: ${({ $height }) => $height + 'px'};
  width: ${({ $width }) => $width + 'px'};
`;

const SkeletonReferral = () => (
  <PaddedBlurb data-cy='referral-skeleton'>
    <ReferralHeader style={{ gap: '8px' }}>
      <SkeletonBlank $height={14} $width={60} $rounding={4} />
      <SkeletonBlank $height={22} $width={90} $rounding={16} />
    </ReferralHeader>
    <ReferralBodyContainer>
      <SkeletonBlank $height={12} $width={180} $margin={8} />
    </ReferralBodyContainer>
    <SkeletonBlank $height={32} $width={120} $rounding={3} $margin={16} />
  </PaddedBlurb>
);
